import React, { useEffect, useState } from 'react';
import { postFetchRequest } from '../store/requests';
import { makeUrl, errorCallback } from '../constant';

const PROXY_CONFIG_URL = '/api/v1/application/autobase/proxy-config/';

const SettingsCard = ({
    title,
    hint,
    value,
    onChange,
    placeholder,
    rows,
    onSave,
    saving,
    busy,
    saveLabel,
    wide,
}) => (
    <section className={`autobase-settings__card${wide ? ' autobase-settings__card--wide' : ''}`}>
        <div className="autobase-settings__card-head">
            <h2 className="autobase-settings__subtitle">{title}</h2>
            <p className="autobase-settings__hint">{hint}</p>
        </div>
        <textarea
            className="autobase-settings__textarea"
            rows={rows}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={busy}
        />
        <div className="autobase-settings__card-actions">
            <button
                type="button"
                className="order-button"
                onClick={onSave}
                disabled={busy}
            >
                {saving ? 'Сохранение...' : saveLabel}
            </button>
        </div>
    </section>
);

const AutobaseTab = ({ isCollapsed }) => {
    const [proxyText, setProxyText] = useState('');
    const [residentialProxyText, setResidentialProxyText] = useState('');
    const [proxyStatus, setProxyStatus] = useState(null);
    const [isProxyLoading, setIsProxyLoading] = useState(false);
    const [isProxySaving, setIsProxySaving] = useState(false);
    const [isResidentialSaving, setIsResidentialSaving] = useState(false);
    const [mouserKeysText, setMouserKeysText] = useState('');
    const [digikeyKeysText, setDigikeyKeysText] = useState('');
    const [isMouserKeysSaving, setIsMouserKeysSaving] = useState(false);
    const [isDigikeyKeysSaving, setIsDigikeyKeysSaving] = useState(false);
    const [proxyError, setProxyError] = useState(null);
    const [proxySuccess, setProxySuccess] = useState(null);

    const isProxyBusy = isProxyLoading || isProxySaving || isResidentialSaving || isMouserKeysSaving || isDigikeyKeysSaving;
    const workspaceClass = isCollapsed === null ? '' : (isCollapsed ? 'workspace_collapsed' : 'workspace_full-width');

    const applyProxyConfig = (data) => {
        const proxies = Array.isArray(data.proxies) ? data.proxies : [];
        const residential = Array.isArray(data.residentialProxies) ? data.residentialProxies : [];
        const mouserKeys = Array.isArray(data.mouserApiKeys) ? data.mouserApiKeys : [];
        const digikeyKeys = Array.isArray(data.digikeyApiKeys) ? data.digikeyApiKeys : [];
        setProxyText(proxies.join('\n'));
        setResidentialProxyText(residential.join('\n'));
        setMouserKeysText(mouserKeys.join('\n'));
        setDigikeyKeysText(digikeyKeys.join('\n'));
        setProxyStatus(data.status || null);
    };

    useEffect(() => {
        setIsProxyLoading(true);
        setProxyError(null);
        setProxySuccess(null);

        fetch(makeUrl([PROXY_CONFIG_URL]), {
            credentials: 'include',
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Ошибка загрузки настроек прокси: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                applyProxyConfig(data);
            })
            .catch((e) => {
                errorCallback(e);
                setProxyError(e.message || 'Не удалось загрузить настройки прокси');
            })
            .finally(() => {
                setIsProxyLoading(false);
            });
    }, []);

    const parseLines = (text) => (text || '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

    const saveConfig = (body, successMessage, setSaving) => {
        setSaving(true);
        setProxyError(null);
        setProxySuccess(null);

        postFetchRequest(
            makeUrl([PROXY_CONFIG_URL]),
            body,
            (data) => {
                applyProxyConfig(data);
                setProxySuccess(successMessage);
                setSaving(false);
            },
            (e) => {
                errorCallback(e);
                setProxyError((e && e.message) || 'Не удалось сохранить настройки');
                setSaving(false);
            },
        );
    };

    return (
        <section className={`workspace ${workspaceClass} autobase-settings`}>
            <h1 className="autobase-settings__title">Настройки автобазы</h1>

            {proxyError && (
                <div className="autobase-settings__alert autobase-settings__alert--error">
                    {proxyError}
                </div>
            )}
            {proxySuccess && (
                <div className="autobase-settings__alert autobase-settings__alert--success">
                    {proxySuccess}
                </div>
            )}
            {proxyStatus && (proxyStatus.mode === 'no_proxies' || proxyStatus.mode === 'fallback_no_proxy') && (
                <div className="autobase-settings__alert autobase-settings__alert--error">
                    Ни один из прокси не работает, система работает в режиме без прокси
                </div>
            )}
            {isProxyLoading && (
                <div className="autobase-settings__loading">Загрузка настроек...</div>
            )}

            <div className="autobase-settings__grid">
                <SettingsCard
                    title="Прокси"
                    hint="Каждая строка — отдельный прокси. Используются для запросов к источникам."
                    placeholder="http://user:pass@host:port"
                    value={proxyText}
                    onChange={setProxyText}
                    rows={10}
                    onSave={() => saveConfig({ proxies: parseLines(proxyText) }, 'Настройки прокси сохранены', setIsProxySaving)}
                    saving={isProxySaving}
                    busy={isProxyBusy}
                    saveLabel="Сохранить прокси"
                />
                <SettingsCard
                    title="Резидентские прокси"
                    hint="Для загрузки фото и даташитов. Каждая строка — отдельный прокси."
                    placeholder="http://user:pass@host:port"
                    value={residentialProxyText}
                    onChange={setResidentialProxyText}
                    rows={10}
                    onSave={() => saveConfig(
                        { residentialProxies: parseLines(residentialProxyText) },
                        'Резидентские прокси сохранены',
                        setIsResidentialSaving,
                    )}
                    saving={isResidentialSaving}
                    busy={isProxyBusy}
                    saveLabel="Сохранить резидентские прокси"
                />
                <SettingsCard
                    wide
                    title="Ключи Mouser"
                    hint="По одному API-ключу в строке."
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={mouserKeysText}
                    onChange={setMouserKeysText}
                    rows={8}
                    onSave={() => saveConfig({ mouserApiKeys: parseLines(mouserKeysText) }, 'Ключи Mouser сохранены', setIsMouserKeysSaving)}
                    saving={isMouserKeysSaving}
                    busy={isProxyBusy}
                    saveLabel="Сохранить ключи Mouser"
                />
                <SettingsCard
                    wide
                    title="Ключи DigiKey"
                    hint="Пара CLIENT_ID::CLIENT_SECRET, по одной паре в строке."
                    placeholder="CLIENT_ID::CLIENT_SECRET"
                    value={digikeyKeysText}
                    onChange={setDigikeyKeysText}
                    rows={8}
                    onSave={() => saveConfig({ digikeyApiKeys: parseLines(digikeyKeysText) }, 'Ключи DigiKey сохранены', setIsDigikeyKeysSaving)}
                    saving={isDigikeyKeysSaving}
                    busy={isProxyBusy}
                    saveLabel="Сохранить ключи DigiKey"
                />
            </div>
        </section>
    );
};

export default AutobaseTab;
